import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, animate, useMotionValue, useMotionValueEvent } from 'framer-motion';
import './JourneyTracker.css';

// Sections matching the actual LandingPage section ids
const SECTIONS = [
  { id: 'hero',         label: 'pick-up',     icon: '🏠', color: '#1B998B' }, // Green
  { id: 'how-it-works', label: 'Packaging',  icon: '⚙️', color: '#1B998B' }, // Red-Orange
  { id: 'features',    label: 'In transit', icon: '⚡', color: '#2EC4B6' }, // Teal
  { id: 'sea-tracking', label: 'Sea',      icon: '🚢', color: '#022C43' }, // Navy
  { id: 'air-tracking', label: 'Air',      icon: '✈️', color: '#0A3D5C' }, // Light Navy
  { id: 'coverage',    label: 'delivered', icon: '🌍', color: '#1B998B' }, // Yellow
];

// SVG Car Component
const CarIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className}
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5H6.5C5.84 5 5.28 5.42 5.08 6.01L3 12V20C3 20.55 3.45 21 4 21H5C5.55 21 6 20.55 6 20V19H18V20C18 20.55 18.45 21 19 21H20C20.55 21 21 20.55 21 20V12L18.92 6.01ZM6.85 7H17.14L18.22 10H5.77L6.85 7ZM6 15C5.17 15 4.5 14.33 4.5 13.5C4.5 12.67 5.17 12 6 12C6.83 12 7.5 12.67 7.5 13.5C7.5 14.33 6.83 15 6 15ZM18 15C17.17 15 16.5 14.33 16.5 13.5C16.5 12.67 17.17 12 18 12C18.83 12 19.5 12.67 19.5 13.5C19.5 14.33 18.83 15 18 15Z" 
      fill="white"
    />
  </svg>
);

// SVG Airplane Component (pointing up)
const AirplaneIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className}
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M21 16V14L13 9V3.5C13 2.67 12.33 2 11.5 2C10.67 2 10 2.67 10 3.5V9L2 14V16L10 13.5V19L8 20.5V22L11.5 21L15 22V20.5L13 19V13.5L21 16Z" 
      fill="white"
    />
  </svg>
);

const JourneyTracker = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [activeSection, setActiveSection] = useState('hero');
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollTimeout, setScrollTimeout] = useState(null);
  const [sectionPositions, setSectionPositions] = useState([]);
  const [scrollDir, setScrollDir] = useState('down');
  
  const [documentOffset, setDocumentOffset] = useState({ start: 0, end: 1000 });

  // Get the precise scroll of the document body
  const { scrollY } = useScroll();
  
  // Smooth out the scroll progress for the car
  const smoothProgress = useSpring(scrollY, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Detect scroll direction for icon swapping
  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious();
    if (latest > previous && latest > 5) {
      setScrollDir('down');
    } else if (latest < previous) {
      setScrollDir('up');
    }
  });

  // Manage dynamic active color string internally for smooth transitions
  const activeColor = useMotionValue(SECTIONS[0].color);

  // State for road height to span the page nicely
  const [roadHeight, setRoadHeight] = useState(window.innerHeight * 0.7);

  // Transform scrollY directly into a localized progress
  const carY = useTransform(
    smoothProgress, 
    [documentOffset.start, documentOffset.end], 
    [0, roadHeight]
  );

  // Update road height on window resize
  useEffect(() => {
    const handleResize = () => setRoadHeight(window.innerHeight * 0.7);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update dynamic color when active section changes
  useEffect(() => {
    const activeSecData = SECTIONS.find(s => s.id === activeSection);
    if (activeSecData) {
      animate(activeColor, activeSecData.color, { duration: 0.5 });
    }
  }, [activeSection, activeColor]);

  // Handle scroll state (moving vs stopped)
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);
      
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // Consider scrolling stopped after 400ms of no scroll events
      const timeout = setTimeout(() => {
        setIsScrolling(false);
      }, 400);
      
      setScrollTimeout(timeout);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [scrollTimeout]);

  // Setup Intersection Observer to detect active section
  useEffect(() => {
    // Wait a frame to ensure DOM is ready
    setTimeout(() => {
      const observerPairs = [];
      const positions = [];
      
      const observerCallback = (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      };

      const observer = new IntersectionObserver(observerCallback, {
        root: null,
        rootMargin: '-30% 0px -30% 0px', // Trigger when section is in middle of screen
        threshold: 0
      });

      SECTIONS.forEach((section, index) => {
        const el = document.getElementById(section.id);
        if (el) {
          observer.observe(el);
          // Calculate relative position (0 to 1) for the pin placement
          // Note: This is a simplified calculation. Real accurate placement requires measuring total scrollable height.
          const pos = index / (SECTIONS.length - 1);
          positions.push({ id: section.id, position: pos });
        }
      });
      
      setSectionPositions(positions);

      // Set the global layout bounds so car knows its absolute track
      const heroEl = document.getElementById('hero');
      if (heroEl) {
        setDocumentOffset({
          start: heroEl.offsetTop,
          end: document.body.scrollHeight - window.innerHeight
        });
      }

      return () => observer.disconnect();
    }, 500);
  }, []);

  if (isMobile) return null;

  return (
    <div className="jt-container">
      <div className="jt-panel">
        <div className="jt-title">Journey</div>
        
        <div className="jt-road-container" style={{ height: roadHeight }}>
          {/* Dashed Road Line */}
          <div className="jt-road-line"></div>
          
          {/* Section Pins */}
          {sectionPositions.map((sec) => {
            const sectionData = SECTIONS.find(s => s.id === sec.id);
            const isActive = activeSection === sec.id && !isScrolling;
            // Localized boolean using transformed progress
            const isPassed = carY.get() >= (sec.position * roadHeight);
            
            return (
              <div 
                key={sec.id}
                className={`jt-pin ${isActive ? 'active' : ''} ${isPassed ? 'passed' : ''}`}
                style={{ top: `${sec.position * roadHeight}px` }}
              >
                <motion.div 
                  className="jt-pin-dot"
                  style={{ 
                    backgroundColor: isActive ? activeColor : (isPassed ? sectionData.color : '#8B9BB4'),
                    boxShadow: isActive ? activeColor.get() + '40 0px 0px 0px 4px, ' + activeColor.get() + ' 0px 0px 12px' : 'none',
                    scale: isActive ? 1.6 : 1
                  }}
                />
                <motion.div 
                  className="jt-pin-label"
                  style={{ backgroundColor: isActive ? activeColor : 'rgba(2, 44, 67, 0.9)' }}
                >
                  <span className="jt-pin-icon">{sectionData?.icon}</span>
                  {sectionData?.label}
                </motion.div>
              </div>
            );
          })}
          
          {/* Active Car & Progress Fill is removed so dots have full priority */}
          
          <motion.div 
            className={`jt-car-container ${isScrolling ? 'moving' : 'stopped'}`}
            style={{ y: carY }}
          >
            <motion.div 
               className="jt-car-glow"
               style={{ backgroundColor: activeColor }}
            />
            {scrollDir === 'up' ? (
              <AirplaneIcon className="jt-car-svg" />
            ) : (
              <CarIcon className="jt-car-svg" />
            )}
          </motion.div>
          
        </div>
      </div>
    </div>
  );
};

export default JourneyTracker;
